<template>
	<div>
		<!-- heading row -->
		<v-row class="mx-2 my-1">
			<v-col cols="12" md="2" align-self="center">
				<h2 class="text-left">All Tasks</h2>
			</v-col>

			<v-spacer></v-spacer>

			<v-col cols="12" md="2" sm="6" align-self="center" v-if="currentUser.privileges.canManageTags">
				<v-btn 
					color="primary"
					rounded
					:to="{ name: 'ManageTaskTags' }"
					block
					id="manage-tags"
				> 
					Manage Tags 
				</v-btn>
			</v-col>

			<v-col cols="12" md="2" sm="6" align-self="center" v-if="currentUser.privileges.canAddTasks">
				<v-btn
					color="primary"
					@click="addTaskDialog = true"
					rounded
					block
					id="add-task"
				> 
					Add Task 
				</v-btn>
			</v-col>
		</v-row>

		<v-row justify="center" class="mx-2 my-1">
			
			<v-col cols="12" md="6" align-self="center">
				<v-text-field
					v-model="search"
					prepend-inner-icon="mdi-magnify"
					label="Search"
					single-line
					hide-details
					outlined
					dense
					clearable
					class="rounded-input"
					@keyup="filterForTasks()"
				></v-text-field>
			</v-col>

			<!-- <v-spacer></v-spacer> -->

			<v-col cols="12" md="3" sm="6" align-self="center">
				<v-switch
					v-model="filter.completion_status"
					inset
					label="Show Completed"
					class="mx-2 filter-switch"
					color="primary"
					:disabled="isLoading"
					@change="filterForTasks()"
					id="is-complete"
				>
				</v-switch>
			</v-col>
		</v-row>

		<v-row class="my-1 mx-3" justify="center" no-gutters>
			<v-col cols="12" md="3" sm="6" align-self="center">
				<!-- filter by creator -->
				<v-autocomplete
					:items="creatorItems"
					v-model="filter.creator"
					outlined
					item-text="full_name"
					item-value="id"
					color="primary"
					label="Filter by Creator"
					hide-details
					background-color="transparent"
					class="my-1 mx-2 rounded-input"
					clearable
					multiple
					@change="filterForTasks()"
					small-chips
					deletable-chips
				>
				</v-autocomplete>
			</v-col>
			
			<v-col cols="12" md="3" sm="6" align-self="center">
				<!-- filter by assignee -->
				<v-autocomplete
					:items="assigneeItems"
					item-text="full_name"
					item-value="id"
					v-model="filter.assignee"
					outlined
					color="primary"
					label="Filter by Assignee"
					hide-details
					clearable
					background-color="transparent"
					class="my-1 mx-2 rounded-input"
					multiple
					@change="filterForTasks()"
					small-chips
					deletable-chips
				>
				</v-autocomplete>
			</v-col>

			<v-col cols="12" md="3" sm="6" align-self="center">
				<!-- filter by tags -->
				<v-autocomplete
					:items="tagItems"
					item-text="name"
					item-value="id"
					v-model="filter.tags"
					outlined
					color="primary"
					label="Filter by Tags"
					hide-details
					clearable
					background-color="transparent"
					class="my-1 mx-2 rounded-input"
					multiple
					@change="filterForTasks()"
				>
					<template v-slot:selection="{ item,index }">
						<v-chip
							small
							:selection="item.selected"
							:color="item.color ? item.color : 'grey'"
							class="my-1"
							close
							@click:close="deleteChip(index)"
						>
							<div v-if="item.name">
								{{ item.name }}
							</div>
						</v-chip>
					</template>
				</v-autocomplete>
			</v-col>
		</v-row>

		<!-- default filler text if no tasks available -->
		<v-row v-if="!listItemCount" class="my-2" justify="center">
			<v-col cols="12" align-self="center" v-if="!isLoading">
				<h1 v-if="!filter.completion_status">
					All tasks completed! 
				</h1>
				<h1 v-else>
					No tasks available. 
				</h1>

				<v-icon
					x-large 
					color="green"
				>
					mdi-playlist-check
				</v-icon>

				<h3 v-if="currentUser.privileges.canViewOwnTasks && !currentUser.privileges.canViewAllTasks && !filter.assignee.length && !filter.creator.length">
					Note: Please use any one of the assignee or creator filter at all times
				</h3>
			</v-col>
		</v-row>

		<v-row justify="center" class="my-1 mx-2">
			<!-- component to show all tasks -->
			<v-col cols="12" md="9" sm="12">
				<v-card :class="!listItemCount ? 'empty-card' : ''">
					<ViewTasks
						:visible="true"
						:items="items"
						:showAssigneeName="false"
						:hideSnackbar="hideSnackbar"
						:pageCompletionFilter="filter.completion_status"
						@fetchFreshTasks="filterForTasks"
						@applyTagFilter="applyTagFilterCallback"
						@replaceTask="replaceTask"
						@deletedTaskFromDialog="deletedTaskFromDialog"
						@changeTaskInDialog="changeTaskInDialog"
						@listCount="updateView"
					/>
				</v-card>
			</v-col>
		</v-row>

		<mugen-scroll :handler="fetchNextTasks" :should-handle="!loading">
			<div v-if="pagination.next" class="mb-4 mt-1">
				<h3>
					Scroll down for more tasks
				</h3>
			</div>
		</mugen-scroll>
		
		<AddTaskDialog
			:visible="addTaskDialog"
			title="Add Task"
			@closeAddTaskDialog="closeTaskDialog"
			@fetchNewTasks="filterForTasks"
		/>

		<!-- snackbar -->
		<v-snackbar v-model="snackbar.visible" :color="snackbar.color" multi-line>
			{{ snackbar.message }}
			<template v-slot:action="{ attrs }">
				<v-btn dark text v-bind="attrs" @click="snackbar.visible = false">
					Close
				</v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script>
import api from "@api/index"
import { mapActions, mapGetters } from "vuex"
import endpoints from "@api/repository"
import Helper from "@utils/misc"
import moment from "moment"
import ViewTasks from "@components/task/ViewTasks.vue"
import AddTaskDialog from "@components/task/AddTaskDialog.vue"
import MugenScroll from 'vue-mugen-scroll'
import TourHelper from "@utils/tour_helper";

export default {
	name: 'AllTasks',

	components: {
		AddTaskDialog,
		ViewTasks,
		MugenScroll,
	},

	props: ['tagId'],

	data() {
		return {
			moment: moment,
			loading: false,
			snackbar: {
				visible: false,
				message: "",
				color: "dark",
			},
			search: '',
			addTaskDialog: false,

			filter: {
				creator: [],
				assignee: [],
				tags: [],
				completion_status: false
			},

			// items for filters
			creatorItems: [],
			assigneeItems: [],
			tagItems: [],

			pagination: {
				page: 1,
				count: null,
				next: null,
				previous: null,
			},

			// store task items
			items: [],

			hideSnackbar: false,

			listItemCount: null,
			closeChip:true,
		}
	},

	computed: {
		...mapGetters([
			"currentUser",
			"accessToken",
			"isLoading",
			"currentInstitution",
		]),

		apiEssentials() {
			return {
				accessToken: this.accessToken,
				setLoading: this.setLoading,
				handleErrorsFunction: this.handleApiErrors,
			};
		},
	},

	methods: {
		...mapActions(["setLoading"]),

		handleTour() {
			var steps = [];
			steps.push({id: '1', title: "Welcome to Task Management", description: "This Section helps you to add and manage tasks.", videoLink: "https://youtube.com"});
			steps.push({id: '2', description: "Add and edit new/existing tags." ,el: '#manage-tags'});
			steps.push({id: '3', description: "Add new task by filling necessary information." ,el: '#add-task'});
			steps.push({id: '4', description: "Shows all completed tasks if switched to 'Show Complete'." ,el: '#is-complete'});
			steps.push({id: '5', title:"General Instructions", description: "- Click on square check-box to change it's status from completed to uncomplete or vice-versa.<br>- click on task title to open edit dialog. <br>- hover over note icon for description."});
			TourHelper.startTour(this, "tasks", steps);
    },

		// handle api errors
		async handleApiErrors(err) {
			this.setLoading(false);
			console.log(err);
			if (err.response) {
				this.snackbar.message = "Unable to process request";
				this.snackbar.color = "error";
				this.snackbar.visible = true;
			}
		},

		// fetch filter items via api
		async getFilterItems() {			
			var urlForStaff = endpoints.getAllStaff + `?all=true`
			var urlForTags = endpoints.taskTags

			var response1 = await api.call(this.apiEssentials, urlForStaff)		// for creator, assignee
			var response2 = await api.call(this.apiEssentials, urlForTags)		// for task tags

			if(response1) {
				if(this.currentUser.privileges.canViewOwnTasks && !this.currentUser.privileges.canViewAllTasks) {
					this.creatorItems = response1.filter(item => item.id == this.currentUser.id)
					this.assigneeItems = this.creatorItems
				}
				else {
					this.creatorItems = response1
					this.assigneeItems = response1
				}
			}

			if(response2) {
				this.tagItems = response2
			}

			// set default filter for assignee - current user
			if(this.assigneeItems.length) {
				this.filter.assignee.push(this.currentUser.id)
			}
		},

		async fetchAllTasks() {
			var url = Helper.addUrlParams(endpoints.tasks, [`assignee__in=${this.currentUser.id}`, 'completed=false', 'is_sub_task=false'])

			var response = await api.call(this.apiEssentials, url)

			if(response) {
				this.items = response.results
				this.pagination.next = response.next
				this.pagination.previous = response.previous
				this.pagination.count = response.count
			}
		},

		// trigger function for mugen scroll to fetch tasks from next page
		async fetchNextTasks() {
			this.loading = true
			
			if(this.pagination.next && !this.isLoading) {
				var url = this.pagination.next
				var response1 = await api.call(this.apiEssentials, url)

				if(response1) {
					this.pagination.next = response1.next
					this.pagination.count = response1.count
					this.pagination.previous = response1.previous

					response1.results.map(task => this.items.push(task))
				}
			}

			this.loading = false
		},

		// api call to fetch tasks according to filters, search
		async filterForTasks() {
			this.hideSnackbar = !this.hideSnackbar

			var url = Helper.addUrlParams(endpoints.tasks, 'is_sub_task=false')

			if(this.currentUser.privileges.canViewOwnTasks && !this.currentUser.privileges.canViewAllTasks){
				if(!this.filter.assignee.length && !this.filter.creator.length){
					return;
				}
			}

			if(this.filter.assignee.length) {
				url = Helper.addUrlParams(url,`assignee__in=${this.filter.assignee.toString()}`)
			}

			if(this.filter.creator.length) {
				url = Helper.addUrlParams(url,`creator__in=${this.filter.creator.toString()}`)
			}

			if(this.filter.tags.length) {
				url = Helper.addUrlParams(url,`task_tags__id__in=${this.filter.tags.toString()}`)
			}

			if(this.search) {
				url = Helper.addUrlParams(url,`search=${this.search}`)
			}

			url = Helper.addUrlParams(url,`completed=${this.filter.completion_status}`)

			var response = await api.call(this.apiEssentials, url)
			if(response) {
				this.items = response.results
				this.pagination.count = response.count
				this.pagination.next = response.next
				this.pagination.previous = response.previous
			}
		},

		replaceTask(newTask){
			var newTaskIndex = this.items.findIndex(t => t.id == newTask.id)
			this.$set(this.items, newTaskIndex, newTask);
		},

		deletedTaskFromDialog(id, is_sub_task, parent){
			if(!is_sub_task){
				var index = this.items.findIndex(t => t.id == id)
				this.items.splice(index, 1)
			}else{
				var parent_index = this.items.findIndex(t => t.id == parent);
				var sub_task_index = this.items[parent_index].sub_tasks.findIndex(t => t.id == id);
				this.items[parent_index].sub_tasks.splice(sub_task_index, 1);
			}
		},

		async changeTaskInDialog(id){
			var url = endpoints.tasks+`${id}/`
			var response = await api.call(this.apiEssentials, url);
			if(response){
				var oldTaskIndex = this.items.findIndex(t => t.id == id);
				this.items[oldTaskIndex] = response;
				this.$set(this.items, oldTaskIndex, response);
			}
		},

		applyTagFilterCallback(tagID) {
			if(tagID) {
				if(!this.filter.tags.includes(tagID)) {
					this.filter.tags.push(tagID)
					this.filterForTasks()
				}
			}
		},

		closeTaskDialog() {
			this.addTaskDialog = false;
		},

		updateView(count) {
			this.listItemCount = count
		},

		deleteChip(index){
			this.filter.tags.splice(index, 1)
			this.filterForTasks()
		},
	},

	watch: {},

	async created() {
		// if tagId received via props -> set tag in filter
		if(this.tagId) {
			this.filter.tags.push(this.tagId)
		}		

		await this.getFilterItems()
		this.filterForTasks()
		this.handleTour()
	}
}
</script>

<style>
.empty-card {
	visibility: hidden;
}

.filter-switch .v-input__slot {
    /* To center the switch element */
  justify-content: center !important;
}

.filter-switch .v-label {
    /* To remove the switch label padding */
  display: inline !important;
  position: relative !important;
  flex: none !important;
}
</style>